import { useMemo } from 'react';
import { X, PanelLeftOpen, PanelLeftClose } from 'lucide-react';
import { SidebarProvider } from './SidebarContext.js';

/**
 * Sidebar shell — a vertical navigation container.
 *
 * Desktop: inline sidebar that can collapse to icon-only width.
 * Mobile (<md): hidden by default, slides in as a drawer overlay.
 *
 * @param {object}   props
 * @param {React.ReactNode} props.children
 * @param {React.ReactNode} [props.logo]
 * @param {React.ReactNode} [props.footer]
 * @param {boolean}  [props.collapsed=false]       - Desktop collapsed state
 * @param {boolean}  [props.mobileOpen=false]       - Mobile drawer open state
 * @param {Function} [props.onClose]                - Close the mobile drawer
 * @param {Function} [props.onToggleCollapse]       - Toggle desktop collapsed state
 * @param {string}   [props.className]
 */
const Sidebar = ({
  children,
  logo,
  footer,
  className = '',
  collapsed = false,
  mobileOpen = false,
  onClose,
  onToggleCollapse,
}) => {
  // When running inside the Core Shell, the shell owns the sidebar — suppress the app-level one
  if (window.__AIMS_SHELL__?.mounted) return null;
  const ctx = useMemo(
    () => ({ collapsed, closeMobile: onClose ?? (() => {}) }),
    [collapsed, onClose],
  );

  return (
    <SidebarProvider value={ctx}>
      {/* Mobile backdrop */}
      <div
        className={[
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 md:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={[
          'flex flex-col border-r border-edge bg-surface transition-all duration-200 ease-in-out',
          // Mobile: fixed drawer
          'fixed inset-y-0 left-0 z-50 w-sidebar shadow-xl',
          'md:relative md:z-auto md:shadow-none',
          // Desktop width
          collapsed ? 'md:w-16' : 'md:w-sidebar',
          // Mobile slide
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Header — only rendered when there's content to show */}
        {(logo || onClose || onToggleCollapse) && (
          <div
            className={[
              'flex h-topnav flex-shrink-0 items-center border-b border-edge',
              // Collapsed: center toggle on desktop; keep drawer close aligned end on mobile
              collapsed ? 'justify-end md:justify-center' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {!collapsed && logo && (
              <div className="flex-1 overflow-hidden px-4">{logo}</div>
            )}

            <div
              className={[
                'flex items-center gap-1',
                collapsed ? 'px-2 md:px-0' : 'px-2',
              ].join(' ')}
            >
              {onClose && (
                <button
                  onClick={onClose}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-surface-hover hover:text-content-hover md:hidden"
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              )}

              {onToggleCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className="hidden rounded-md p-1.5 text-gray-400 hover:bg-surface-hover hover:text-content-hover md:flex"
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                </button>
              )}
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-2 py-4 md:px-3">
          <div className="flex flex-col gap-1">{children}</div>
        </nav>

        {footer && !collapsed && (
          <div className="flex-shrink-0 border-t border-edge p-3">
            {footer}
          </div>
        )}
      </aside>
    </SidebarProvider>
  );
};

export default Sidebar;
